<template>
    <!-- SQL 执行控制台 -->
    <el-drawer v-model="drawerVisible" class="dbconsole-drawer" direction="btt" :size="drawerSize" :show-close="false"
        :close-on-click-modal="false" :close-on-press-escape="false" destroy-on-close @opened="onOpened">
        <template #header="{ close, titleId, titleClass }">
            <div :id="titleId" :class="[titleClass, 'dbconsole-head']">
                <span class="dbconsole-head__name">{{ conn.name }}</span>
                <span class="dbconsole-head__addr">{{ conn.host }}:{{ conn.port }}</span>
                <el-tag size="small" :type="conn.canWrite ? 'warning' : 'info'" effect="plain">
                    {{ conn.canWrite ? '可写' : '只读' }}</el-tag>
                <el-tag v-if="conn.prodLock" size="small" type="danger" effect="plain">生产防护</el-tag>
            </div>
            <el-button class="dbconsole-close" type="danger" plain @click="confirmClose(close)">
                <el-icon class="el-icon--left">
                    <CircleCloseFilled />
                </el-icon>
                关闭
            </el-button>
        </template>

        <div class="dbconsole-body">
            <!-- 左侧：SQL 块编辑区 -->
            <section class="dbconsole-left">
                <div class="dbconsole-left__head">
                    <span class="dbconsole-title">SQL 编辑</span>
                    <el-button class="dbconsole-add" type="primary" plain @click="highlightNew(addBlock())">
                        <el-icon class="el-icon--left">
                            <Plus />
                        </el-icon>
                        新增块
                    </el-button>
                </div>

                <!-- 块列表：独立滚动容器，滚动条只出现在标题栏下方（与右侧 .dbconsole-right__list 同构） -->
                <div ref="leftList" class="dbconsole-left__list">
                    <div v-if="blocks.length === 0" class="dbconsole-empty">
                        <el-icon :size="22">
                            <EditPen />
                        </el-icon>
                        <p>点击「新增块」开始编写 SQL</p>
                    </div>
                    <!-- 进入时展开动画 + 短暂高亮，便于用户看清是哪个块被新增/载入 -->
                    <transition-group name="dbconsole-block-anim">
                        <div class="dbconsole-block" :class="{ 'is-fresh': block._fresh }" v-for="(block, bi) in blocks"
                            :key="block.uid">
                        <div class="dbconsole-block__head">
                            <!-- 左上角序号标志（倒序：新增的块在最上面，序号最大） -->
                            <span class="dbconsole-block__no">{{ blocks.length - bi }}</span>
                            <div class="dbconsole-block__title">
                                <span class="dbconsole-block__name">{{ block.name || '未保存' }}</span>
                                <el-tag v-if="block.id && block.isShare" size="small" type="success" effect="plain">已共享</el-tag>
                                <el-select v-model="block.database" class="dbconsole-block__db-select" size="small"
                                    placeholder="选择数据库" filterable allow-create default-first-option :loading="dbLoading">
                                    <el-option v-for="db in databases" :key="db" :label="db" :value="db" />
                                </el-select>
                            </div>
                            <div class="dbconsole-block__ops">
                                <el-button size="small" type="primary" :loading="block.loading" @click="run(block)">
                                    <el-icon class="el-icon--left">
                                        <CaretRight />
                                    </el-icon>
                                    执行
                                </el-button>
                                <el-button size="small" type="warning" @click="openSave(block)">
                                    <el-icon class="el-icon--left">
                                        <Star />
                                    </el-icon>
                                    保存
                                </el-button>
                                <el-button size="small" text @click="copySql(block)">
                                    <el-icon class="el-icon--left">
                                        <DocumentCopy />
                                    </el-icon>
                                    复制
                                </el-button>
                                <el-button size="small" type="danger" @click="removeBlock(block)">移除</el-button>
                            </div>
                        </div>
                        <el-input v-model="block.sqlText" type="textarea" :rows="3" class="dbconsole-block__input"
                            placeholder="在此输入 SQL（Ctrl/Cmd + Enter 执行）" @keydown="onSqlKeydown($event, block)" />

                        <!-- 执行结果（独立子组件：父级重渲染时不连带重算大表格） -->
                        <div class="dbconsole-block__result">
                            <db-result-panel :result="block.result" :error="block.error" :loading="block.loading"
                                @favorite="rows => openFavorite(block, rows)" />
                        </div>
                        </div>
                    </transition-group>
                </div>
            </section>

            <!-- 右侧：SQL 块（服务端分页 + 名称筛选；默认只看自己创建的，可切换查看共享） -->
            <aside class="dbconsole-right">
                <div class="dbconsole-right__head">
                    <span class="dbconsole-title">SQL 块</span>
                    <span class="dbconsole-right__count">{{ blockPage.total }}</span>
                    <div class="dbconsole-right__switch">
                        <span class="dbconsole-right__switch-label">查看共享</span>
                        <el-switch v-model="showShared" size="small" />
                    </div>
                </div>
                <el-input v-model="blockKeyword" class="dbconsole-right__search" size="small" clearable
                    placeholder="按名称筛选">
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>

                <div class="dbconsole-right__list" v-loading="blockLoading">
                    <el-empty v-if="rightBlocks.length === 0"
                        :description="blockKeyword ? '没有匹配的 SQL 块' : '暂无 SQL 块'" :image-size="60" />
                    <div v-for="sb in rightBlocks" :key="sb.id" class="dbconsole-shared">
                        <div class="dbconsole-shared__head" @click="toggleExpand(sb)">
                            <!-- 没有结果收藏时不展示展开标志 -->
                            <el-icon v-if="sb.favoriteCount > 0" class="dbconsole-shared__arrow"
                                :class="{ 'is-open': sb._expanded }">
                                <ArrowRight />
                            </el-icon>
                            <div class="dbconsole-shared__info">
                                <div class="dbconsole-shared__name">{{ sb.name }}</div>
                                <!-- 数据库/共享等标签单独一行，避免把 SQL 名称挤变形 -->
                                <div v-if="sb.databaseName || sb.isShare || !sb.ownedByMe" class="dbconsole-shared__meta">
                                    <el-tag v-if="sb.databaseName" size="small" type="info" effect="plain">
                                        {{ sb.databaseName }}</el-tag>
                                    <el-tag v-if="sb.isShare" size="small" type="success" effect="plain">共享</el-tag>
                                    <span v-if="!sb.ownedByMe" class="dbconsole-shared__other">他人</span>
                                </div>
                                <div class="dbconsole-shared__desc">{{ sb.description || '—' }}</div>
                            </div>
                            <!-- 操作菜单：载入 / 编辑 / 共享切换 / 删除 -->
                            <el-dropdown class="dbconsole-shared__ops" trigger="click"
                                @command="cmd => onBlockCommand(cmd, sb)">
                                <el-button size="small" text @click.stop>
                                    <el-icon>
                                        <MoreFilled />
                                    </el-icon>
                                </el-button>
                                <template #dropdown>
                                    <el-dropdown-menu>
                                        <el-dropdown-item command="load" class="dbconsole-mi dbconsole-mi--load">
                                            <el-icon>
                                                <Download />
                                            </el-icon>载入
                                        </el-dropdown-item>
                                        <el-dropdown-item command="edit" :disabled="!sb.ownedByMe" class="dbconsole-mi">
                                            <el-icon>
                                                <EditPen />
                                            </el-icon>编辑
                                        </el-dropdown-item>
                                        <el-dropdown-item command="share" :disabled="!sb.ownedByMe"
                                            class="dbconsole-mi dbconsole-mi--share">
                                            <el-icon>
                                                <Share />
                                            </el-icon>{{ sb.isShare ? '取消共享' : '共享' }}
                                        </el-dropdown-item>
                                        <el-dropdown-item command="delete" :disabled="!sb.ownedByMe" divided
                                            class="dbconsole-mi dbconsole-mi--delete">
                                            <el-icon>
                                                <Delete />
                                            </el-icon>删除
                                        </el-dropdown-item>
                                    </el-dropdown-menu>
                                </template>
                            </el-dropdown>
                        </div>

                        <div v-if="sb._expanded" class="dbconsole-shared__body" v-loading="sb._favLoading">
                            <div v-if="!sb._favLoaded" class="dbconsole-shared__hint">加载中…</div>
                            <template v-else>
                                <div v-if="(sb._favList || []).length === 0" class="dbconsole-shared__hint">暂无结果收藏</div>
                                <div v-for="fav in (sb._favList || [])" :key="fav.id" class="dbconsole-fav">
                                    <div class="dbconsole-fav__main">
                                        <div class="dbconsole-fav__name">
                                            {{ fav.favoriteName }}
                                            <el-tag v-if="fav.isShare" size="small" type="success" effect="plain">共享</el-tag>
                                        </div>
                                        <div class="dbconsole-fav__meta">{{ fav.rowCount }} 行 · {{ fav.createTime }}</div>
                                    </div>
                                    <el-link type="primary" @click="viewFavorite(fav)">查看</el-link>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <!-- 底部悬浮胶囊分页条（每页 100 条） -->
                <div v-if="blockPage.total > blockPage.size" class="dbconsole-pager">
                    <el-pagination small background layout="prev, pager, next" :total="blockPage.total"
                        :page-size="blockPage.size" :current-page="blockPage.current" :pager-count="5"
                        @current-change="onBlockPageChange" />
                </div>
            </aside>
        </div>

        <!-- 保存 SQL 块 -->
        <el-dialog class="dbman-dialog" :title="saveForm.mode === 'create' ? '保存 SQL 块' : '编辑 SQL 块'"
            v-model="saveForm.dialog" width="min(520px, 92vw)" append-to-body>
            <el-form label-width="90px">
                <el-form-item label="名称">
                    <el-input v-model="saveForm.name" placeholder="请输入名称" maxlength="100" />
                </el-form-item>
                <el-form-item label="描述">
                    <el-input type="textarea" :rows="3" v-model="saveForm.description" maxlength="200"
                        placeholder="可选，便于以后查阅" />
                </el-form-item>
                <el-form-item label="共享">
                    <div class="dbman-switch-row">
                        <el-switch v-model="saveForm.isShare" />
                        <span class="dbman-tips">开启后，该连接团队成员均可查看并执行</span>
                    </div>
                </el-form-item>
            </el-form>
            <template #footer>
                <div class="dbman-dialog__foot">
                    <el-button @click="saveForm.dialog = false">取 消</el-button>
                    <el-button type="primary" :loading="saveForm.saving" @click="doSave">保 存</el-button>
                </div>
            </template>
        </el-dialog>

        <!-- 收藏所选行 -->
        <el-dialog class="dbman-dialog" title="收藏所选行" v-model="favoriteForm.dialog" width="min(520px, 92vw)"
            append-to-body>
            <el-form label-width="90px">
                <el-form-item label="名称">
                    <el-input v-model="favoriteForm.name" placeholder="请输入收藏名称" maxlength="100" />
                </el-form-item>
                <el-form-item label="描述">
                    <el-input type="textarea" :rows="3" v-model="favoriteForm.description" maxlength="200"
                        placeholder="可选，便于以后查阅" />
                </el-form-item>
                <el-form-item label="共享">
                    <div class="dbman-switch-row">
                        <el-switch v-model="favoriteForm.isShare" />
                        <span class="dbman-tips">开启后，该连接团队成员均可查看</span>
                    </div>
                </el-form-item>
            </el-form>
            <template #footer>
                <div class="dbman-dialog__foot">
                    <el-button @click="favoriteForm.dialog = false">取 消</el-button>
                    <el-button type="primary" :loading="favoriteForm.saving" @click="doFavorite">保 存</el-button>
                </div>
            </template>
        </el-dialog>

        <!-- 查看收藏 -->
        <el-dialog class="dbman-dialog" title="收藏详情" v-model="viewDialog.dialog" width="min(900px, 94vw)"
            append-to-body>
            <div v-if="viewDialog.current.favoriteName" class="dbconsole-view-head">
                <span class="dbconsole-view-name">{{ viewDialog.current.favoriteName }}</span>
                <span class="dbconsole-view-desc">{{ viewDialog.current.description }}</span>
            </div>
            <div v-if="viewColumns.length" class="dbconsole-view-toolbar">
                <el-input v-model="viewDialog.keyword" class="dbconsole-view-search" size="small" clearable
                    placeholder="搜索结果（关键字匹配所有列）">
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>
                <el-button size="small" @click="openFavColumnSetting">
                    <el-icon class="el-icon--left">
                        <Setting />
                    </el-icon>
                    列设置
                </el-button>
                <span v-if="viewDialog.keyword" class="dbconsole-view-match">匹配 {{ viewRowsFiltered.length }} /
                    {{ viewRows.length }} 行</span>
                <span class="dbconsole-view-hint">点击表头可排序；要左右比对哪列，点「列设置」自己固定</span>
            </div>
            <vxe-table v-if="viewColumns.length" ref="xTableFav" :data="viewRowsFiltered" :max-height="420" size="small"
                border stripe show-overflow :row-config="{ keyField: '__idx', isHover: true }"
                :scroll-y="{ enabled: true, gt: 20 }" :sort-config="{ trigger: 'cell', orders: ['asc', 'desc', null] }">
                <vxe-column type="seq" width="56" title="#" />
                <vxe-column v-for="(col, ci) in viewColumns" :key="ci" :field="'c' + ci" :title="col.name"
                    min-width="140" :sortable="true">
                    <template #default="{ row }">
                        <span v-if="row['c' + ci] === null" class="dbconsole-null">NULL</span>
                        <span v-else>{{ row['c' + ci] }}</span>
                    </template>
                </vxe-column>
            </vxe-table>
            <template #footer>
                <div class="dbman-dialog__foot">
                    <el-button type="primary" @click="viewDialog.dialog = false">关 闭</el-button>
                </div>
            </template>
        </el-dialog>
    </el-drawer>
</template>

<script>
import { markRaw } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import debounce from 'lodash/debounce';
import request from '../../../utils/request.js';
import DbResultPanel from './DbResultPanel.vue';

let blockSeq = 0;

export default {
    name: 'DbConsoleDrawer',
    components: { DbResultPanel },
    props: {
        visible: { type: Boolean, default: false },
        conn: { type: Object, default: () => ({}) }
    },
    emits: ['update:visible', 'changed'],
    data() {
        return {
            databases: [],
            dbLoading: false,
            blocks: [],
            // ---- 右侧「SQL 块」面板：服务端分页 ----
            // 是否带上别人共享的块（默认关闭，只看自己创建的）
            showShared: false,
            // 按名称筛选
            blockKeyword: '',
            rightBlocks: [],
            blockLoading: false,
            blockPage: { current: 1, size: 100, total: 0 },
            // 打开抽屉时重置筛选项会触发 watch，用这个标记避免重复请求
            initializing: false,
            saveForm: { mode: 'create', dialog: false, block: null, name: '', description: '', isShare: false, saving: false },
            favoriteForm: { dialog: false, block: null, rows: [], name: '', description: '', isShare: false, saving: false },
            viewDialog: { dialog: false, current: {}, keyword: '' }
        };
    },
    computed: {
        drawerVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit('update:visible', value);
            }
        },
        drawerSize() {
            return document.documentElement.clientWidth <= 768 ? '96%' : '90%';
        },
        viewColumns() {
            return (this.viewDialog.current && this.viewDialog.current.columns) || [];
        },
        viewRows() {
            const rows = (this.viewDialog.current && this.viewDialog.current.rows) || [];
            return rows.map((row, ri) => {
                const obj = { __idx: ri };
                row.forEach((cell, ci) => {
                    obj['c' + ci] = cell;
                });
                return obj;
            });
        },
        // 关键字跨所有列过滤（不区分大小写）
        viewRowsFiltered() {
            const key = (this.viewDialog.keyword || '').trim().toLowerCase();
            if (!key) return this.viewRows;
            const columns = this.viewColumns;
            return this.viewRows.filter(row => {
                for (let ci = 0; ci < columns.length; ci++) {
                    const cell = row['c' + ci];
                    if (cell !== null && cell !== undefined && String(cell).toLowerCase().includes(key)) {
                        return true;
                    }
                }
                return false;
            });
        }
    },
    watch: {
        visible(val) {
            if (val) {
                this.initConsole();
            }
        },
        // 「查看共享」切换：回到第 1 页重新拉
        showShared() {
            if (this.initializing) return;
            this.reloadRightBlocks();
        },
        // 「按名称筛选」：防抖后回到第 1 页重新拉
        blockKeyword() {
            if (this.initializing) return;
            this.debouncedReloadRightBlocks();
        }
    },
    created() {
        // 输入框防抖：拉到 700ms，等用户把一个词敲完再请求，减少服务端压力
        this.debouncedReloadRightBlocks = debounce(() => {
            if (this.visible) this.reloadRightBlocks();
        }, 700);
    },
    methods: {
        initConsole() {
            this.blocks = [];
            // 每次打开都回到默认：不显示他人共享的块、不筛选
            this.initializing = true;
            this.showShared = false;
            this.blockKeyword = '';
            this.blockPage.current = 1;
            this.rightBlocks = [];
            this.addBlock();
            this.loadDatabases();
            this.loadRightBlocks().finally(() => {
                this.initializing = false;
            });
        },
        // 右侧 SQL 块：服务端分页 + 名称筛选
        loadRightBlocks() {
            if (!this.conn.id) return Promise.resolve();
            this.blockLoading = true;
            return request({
                url: '/dbManSqlBlock/pageList',
                method: 'post',
                data: {
                    connId: this.conn.id,
                    keyword: this.blockKeyword,
                    includeShared: this.showShared,
                    current: this.blockPage.current,
                    size: this.blockPage.size
                }
            }).then(res => {
                if (res.code == 0) {
                    this.rightBlocks = res.data || [];
                    this.blockPage.total = res.total || 0;
                }
            }).finally(() => {
                this.blockLoading = false;
            });
        },
        reloadRightBlocks() {
            this.blockPage.current = 1;
            return this.loadRightBlocks();
        },
        onBlockPageChange(page) {
            this.blockPage.current = page;
            this.loadRightBlocks();
        },
        onOpened() {
            // 抽屉打开后自动聚焦首个块（可选）
        },
        // 点击关闭时才允许关闭，且先二次确认（未保存的 SQL 会丢失）
        confirmClose(close) {
            ElMessageBox.confirm('关闭后，未保存的 SQL 语句将会丢失，确定关闭吗？', '关闭确认', {
                confirmButtonText: '确定关闭',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                close();
            }).catch(() => { });
        },
        loadDatabases() {
            this.dbLoading = true;
            return request({
                url: '/dbManExec/databases',
                method: 'post',
                data: { connId: this.conn.id }
            }).then(res => {
                if (res.code == 0) {
                    this.databases = res.meta.databases || [];
                }
            }).finally(() => {
                this.dbLoading = false;
            });
        },
        // ---------- 左侧：SQL 块 ----------
        addBlock(seed) {
            const block = {
                uid: 'blk-' + (++blockSeq),
                id: (seed && seed.id) || '',
                name: (seed && seed.name) || '',
                description: (seed && seed.description) || '',
                isShare: (seed && seed.isShare) || false,
                sqlText: (seed && seed.sqlText) || '',
                // 该块要执行的数据库：已保存的块用其记录值，否则取连接默认库
                databaseName: (seed && seed.databaseName) || '',
                database: (seed && (seed.database || seed.databaseName)) || this.conn.defaultDb || '',
                loading: false,
                result: null,
                error: '',
                // 新加入标记：短暂高亮，便于用户定位新增/载入的块
                _fresh: false
            };
            // 新增的块放到最上方
            this.blocks.unshift(block);
            return block;
        },
        // 高亮新加入的块，并确保滚动到可见位置
        highlightNew(block) {
            block._fresh = true;
            this.$nextTick(() => {
                const list = this.$refs.leftList;
                if (list) list.scrollTo({ top: 0, behavior: 'smooth' });
            });
            setTimeout(() => {
                block._fresh = false;
            }, 2400);
            return block;
        },
        removeBlock(block) {
            const idx = this.blocks.indexOf(block);
            if (idx > -1) this.blocks.splice(idx, 1);
        },
        copySql(block) {
            const text = block.sqlText || '';
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(() => {
                    ElMessage({ message: '复制成功', type: 'success', duration: 1000 });
                });
            } else {
                const ta = document.createElement('textarea');
                ta.value = text;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
                ElMessage({ message: '复制成功', type: 'success', duration: 1000 });
            }
        },
        onSqlKeydown(e, block) {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault();
                this.run(block);
            }
        },
        async run(block) {
            const sql = (block.sqlText || '').trim();
            if (!sql) {
                ElMessage({ message: '请输入 SQL', type: 'warning', duration: 2000 });
                return;
            }
            let confirmText = '';
            if (this.conn.prodLock) {
                try {
                    const result = await ElMessageBox.prompt(
                        '该连接已开启生产防护锁，请输入连接名确认（写操作需要）', '生产防护锁确认',
                        { confirmButtonText: '确定', cancelButtonText: '取消', inputPlaceholder: this.conn.name });
                    confirmText = result.value;
                } catch (e) {
                    return;
                }
            }
            block.loading = true;
            block.error = '';
            // 不清空 block.result：保留上一次结果，执行中在结果上叠加载入遮罩，
            // 返回后一次性替换，避免「先收起再展开」的跳动
            request({
                url: '/dbManExec/query',
                method: 'post',
                data: {
                    connId: this.conn.id,
                    sql: block.sqlText,
                    sqlBlockId: block.id || '',
                    database: block.database || '',
                    maxRows: 200,
                    timeoutSec: 30,
                    confirmText
                }
            }).then(res => {
                if (res.code == 0) {
                    // markRaw：大结果集不做深层响应式，避免拖慢渲染
                    block.result = markRaw(res.meta.result);
                } else {
                    block.error = res.msg || '执行失败';
                }
            }).catch(() => {
                block.error = '执行失败';
            }).finally(() => {
                block.loading = false;
            });
        },
        openSave(block) {
            this.saveForm.mode = block.id ? 'edit' : 'create';
            this.saveForm.block = block;
            this.saveForm.name = block.name || '';
            this.saveForm.description = block.description || '';
            this.saveForm.isShare = !!block.isShare;
            this.saveForm.dialog = true;
        },
        doSave() {
            const form = this.saveForm;
            if (!form.name) {
                ElMessage({ message: '请填写名称', type: 'warning', duration: 2000 });
                return;
            }
            const block = form.block;
            if (!block.sqlText || !block.sqlText.trim()) {
                ElMessage({ message: 'SQL 不能为空', type: 'warning', duration: 2000 });
                return;
            }
            form.saving = true;
            request({
                url: '/dbManSqlBlock/save',
                method: 'post',
                data: {
                    token: this.$store.state.user.token,
                    id: block.id || '',
                    connId: this.conn.id,
                    name: form.name,
                    description: form.description,
                    sqlText: block.sqlText,
                    // 一并记录该块要执行的数据库，便于他人识别在哪个库执行
                    // （右侧已保存的块没有 block.database，用其 databaseName 兜底，避免被清空）
                    databaseName: block.database || block.databaseName || '',
                    isShare: form.isShare,
                    orderIndex: 0
                }
            }).then(res => {
                if (res.code == 0) {
                    const saved = res.meta.dbSqlBlock;
                    block.id = saved.id;
                    block.name = saved.name;
                    block.description = saved.description;
                    block.isShare = saved.isShare;
                    block.databaseName = saved.databaseName || block.database || block.databaseName;
                    ElMessage({ message: '保存成功', type: 'success', duration: 2000 });
                    form.dialog = false;
                    // 刷新右侧列表（当前页）
                    this.loadRightBlocks();
                    this.$emit('changed');
                } else {
                    ElMessage({ message: res.msg || '保存失败', type: 'error', duration: 3000 });
                }
            }).finally(() => {
                form.saving = false;
            });
        },
        // ---------- 右侧：SQL 块（服务端分页 + 名称筛选） ----------
        toggleExpand(sb) {
            // 没有结果收藏的块不展开
            if (!sb.favoriteCount) return;
            sb._expanded = !sb._expanded;
            if (sb._expanded && !sb._favLoaded) {
                this.loadFavorites(sb);
            }
        },
        // 操作菜单分发
        onBlockCommand(cmd, sb) {
            if (cmd === 'load') return this.loadToEditor(sb);
            if (cmd === 'edit') return this.openEditRight(sb);
            if (cmd === 'share') return this.toggleShare(sb);
            if (cmd === 'delete') return this.confirmDeleteBlock(sb);
        },
        // 编辑右侧已保存的 SQL 块（复用保存弹窗，仅创建者可改）
        openEditRight(sb) {
            if (!sb.ownedByMe) {
                ElMessage({ message: '只能编辑自己创建的 SQL 块', type: 'warning', duration: 2000 });
                return;
            }
            this.openSave(sb);
        },
        // 共享 / 取消共享
        toggleShare(sb) {
            const next = !sb.isShare;
            request({
                url: '/dbManSqlBlock/share',
                method: 'post',
                data: { id: sb.id, isShare: next }
            }).then(res => {
                if (res.code == 0) {
                    sb.isShare = next;
                    ElMessage({ message: next ? '已共享' : '已取消共享', type: 'success', duration: 1500 });
                    this.$emit('changed');
                } else {
                    ElMessage({ message: res.msg || '操作失败', type: 'error', duration: 3000 });
                }
            });
        },
        // 删除 SQL 块（不可恢复，连带删除其结果收藏快照）
        confirmDeleteBlock(sb) {
            ElMessageBox.confirm(
                '删除后不可恢复，该 SQL 块下收藏的结果快照也会被同步删除。确定删除吗？',
                '删除确认',
                { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' }
            ).then(() => {
                request({
                    url: '/dbManSqlBlock/delete',
                    method: 'post',
                    data: { id: sb.id }
                }).then(res => {
                    if (res.code == 0) {
                        ElMessage({ message: '已删除', type: 'success', duration: 1500 });
                        const idx = this.rightBlocks.findIndex(x => x.id === sb.id);
                        if (idx > -1) this.rightBlocks.splice(idx, 1);
                        this.blockPage.total = Math.max(0, this.blockPage.total - 1);
                        // 当前页被删空且不是第 1 页时，回退一页
                        if (this.rightBlocks.length === 0 && this.blockPage.current > 1) {
                            this.blockPage.current -= 1;
                        }
                        this.loadRightBlocks();
                        this.$emit('changed');
                    }
                });
            }).catch(() => { });
        },
        loadFavorites(sb) {
            sb._favLoading = true;
            request({
                url: '/dbManRowFavorite/list',
                method: 'post',
                data: { sqlBlockId: sb.id, current: 1, size: 100 }
            }).then(res => {
                if (res.code == 0) {
                    // markRaw：收藏里的 columns/rows 较大，无需深层响应式
                    sb._favList = markRaw(res.data || []);
                    sb._favLoaded = true;
                }
            }).finally(() => {
                sb._favLoading = false;
            });
        },
        loadToEditor(sb) {
            const seed = sb.ownedByMe
                ? { id: sb.id, name: sb.name, description: sb.description, isShare: sb.isShare, sqlText: sb.sqlText, databaseName: sb.databaseName }
                : { name: sb.name + '（副本）', description: sb.description, sqlText: sb.sqlText, databaseName: sb.databaseName };
            this.highlightNew(this.addBlock(seed));
            ElMessage({ message: '已载入到左侧编辑区', type: 'success', duration: 1500 });
        },
        // ---------- 收藏所选行 ----------
        openFavorite(block, rows) {
            if (!block.id) {
                ElMessage({ message: '请先保存该 SQL 块，再进行结果行收藏', type: 'warning', duration: 3000 });
                return;
            }
            if (!rows || rows.length === 0) {
                ElMessage({ message: '请先勾选要收藏的行', type: 'warning', duration: 2000 });
                return;
            }
            if (rows.length > 100) {
                ElMessage({ message: '一次最多收藏 100 行', type: 'warning', duration: 2000 });
                return;
            }
            this.favoriteForm.block = block;
            this.favoriteForm.rows = rows;
            this.favoriteForm.name = (block.name || '结果') + '-结果';
            this.favoriteForm.description = '';
            this.favoriteForm.isShare = false;
            this.favoriteForm.dialog = true;
        },
        doFavorite() {
            const form = this.favoriteForm;
            if (!form.name) {
                ElMessage({ message: '请填写名称', type: 'warning', duration: 2000 });
                return;
            }
            const block = form.block;
            const rows = form.rows || [];
            form.saving = true;
            request({
                url: '/dbManRowFavorite/save',
                method: 'post',
                data: {
                    sqlBlockId: block.id,
                    favoriteName: form.name,
                    description: form.description,
                    columns: block.result ? block.result.columns : [],
                    rows,
                    isShare: form.isShare
                }
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({ message: '已收藏 ' + rows.length + ' 行', type: 'success', duration: 2000 });
                    form.dialog = false;
                    // 更新右侧该块的收藏数量（有收藏后才会出现展开入口）；已展开则刷新其收藏
                    const sb = this.rightBlocks.find(x => x.id === block.id);
                    if (sb) {
                        sb.favoriteCount = (sb.favoriteCount || 0) + 1;
                        if (sb._expanded) {
                            this.loadFavorites(sb);
                        }
                    }
                } else {
                    ElMessage({ message: res.msg || '收藏失败', type: 'error', duration: 3000 });
                }
            }).finally(() => {
                form.saving = false;
            });
        },
        // 打开收藏详情的列设置面板：显隐、拖拽排序、左/右固定，由用户自己点
        openFavColumnSetting() {
            const table = this.$refs.xTableFav;
            if (table && table.openCustom) {
                table.openCustom();
            }
        },
        viewFavorite(fav) {
            request({
                url: '/dbManRowFavorite/get',
                method: 'post',
                data: { id: fav.id }
            }).then(res => {
                if (res.code == 0 && res.meta.dbRowFavorite) {
                    // markRaw：避免对收藏的快照行做深层响应式
                    this.viewDialog.keyword = '';
                    this.viewDialog.current = markRaw(res.meta.dbRowFavorite);
                    this.viewDialog.dialog = true;
                }
            });
        }
    }
};
</script>

<!-- 抽屉挂到 body 下，样式需放非 scoped 块 -->
<style>
.dbconsole-drawer {
    overflow: hidden;
    border-radius: 16px 16px 0 0;
}

.dbconsole-drawer .el-drawer__header {
    gap: 12px;
    margin-bottom: 0;
    padding: 12px 18px;
    border-bottom: 1px solid #eef1f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}

.dbconsole-drawer .el-drawer__title {
    margin: 0;
    flex: 1 1 auto;
    min-width: 0;
}

.dbconsole-drawer .el-drawer__body {
    display: flex;
    flex-direction: column;
    padding: 0;
    overflow: hidden;
}

.dbconsole-drawer .dbconsole-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.dbconsole-drawer .dbconsole-head__name {
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbconsole-drawer .dbconsole-head__addr {
    font-size: 13px;
    color: #8a94a6;
}

.dbconsole-drawer .dbconsole-close {
    flex: 0 0 auto;
    height: 34px;
    border-radius: 9px;
}

.dbconsole-drawer .dbconsole-body {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
}

.dbconsole-drawer .dbconsole-left {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    padding: 12px 16px 18px;
    /* 与右侧同构：容器不滚动，由内部的块列表自己滚动，滚动条才不会盖到标题栏 */
    overflow: hidden;
}

/* 块列表区：自己滚动，标题栏固定 */
.dbconsole-drawer .dbconsole-left__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.dbconsole-drawer .dbconsole-right {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 0 0 340px;
    width: 340px;
    padding: 12px 16px 18px;
    border-left: 1px solid #eef1f6;
    background-color: #fbfcfe;
    overflow: hidden;
}

/* 名称筛选框 */
.dbconsole-drawer .dbconsole-right__search {
    flex: 0 0 auto;
    margin-bottom: 8px;
}

/* 列表区：自己滚动，头部/筛选/分页条固定 */
.dbconsole-drawer .dbconsole-right__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    /* 给底部悬浮分页条留位置，避免最后一张卡片被挡住 */
    padding-bottom: 52px;
}

/* 底部悬浮胶囊分页条 */
.dbconsole-drawer .dbconsole-pager {
    position: absolute;
    left: 50%;
    bottom: 12px;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    padding: 2px 10px;
    border: 1px solid #dbe3ef;
    border-radius: 999px;
    background-color: rgba(255, 255, 255, 0.96);
    box-shadow: 0 8px 20px -10px rgba(28, 47, 84, 0.35);
    z-index: 2;
}

.dbconsole-drawer .dbconsole-title {
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbconsole-drawer .dbconsole-left__head,
.dbconsole-drawer .dbconsole-right__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.dbconsole-drawer .dbconsole-right__count {
    padding: 1px 8px;
    border: 1px solid #d6e0f0;
    border-radius: 999px;
    background-color: #fff;
    color: #2f6fed;
    font-size: 12px;
}

.dbconsole-drawer .dbconsole-right__switch {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
}

.dbconsole-drawer .dbconsole-right__switch-label {
    font-size: 12px;
    color: #8a94a6;
}

.dbconsole-drawer .dbconsole-add {
    margin-left: auto;
}

.dbconsole-drawer .dbconsole-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 40px 0;
    color: #aab4c4;
    font-size: 13px;
}

.dbconsole-drawer .dbconsole-block {
    margin-bottom: 14px;
    /* 边框加深，便于区分相邻的查询块 */
    border: 1px solid #aebfd6;
    border-left: 3px solid #7d9fd0;
    border-bottom: 3px solid #7d9fd0;
    border-radius: 12px;
    background-color: #fff;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(28, 47, 84, 0.08);
    /* 高亮淡出用 */
    transition: box-shadow 0.6s ease;
    /* 把 vxe-table 内部的 z-index 关进卡片自己的层叠上下文：它内部最高用到 99
       （表格外框 .vxe-table--border-line 是 z-index: 10），不隔离就可能穿透到卡片外。
       isolation: isolate 只建层叠上下文、不改变定位，副作用最小。 */
    isolation: isolate;
}

/* 新增/载入的块：从上方淡入、下移并自上而下展开；下方原有卡片同步下移让位 */
/* 注意：这些类必须带上 .dbconsole-drawer 前缀，否则优先级低于基础规则
   `.dbconsole-drawer .dbconsole-block` 的 transition，动画不会生效（会变成瞬间撑开）。 */
.dbconsole-drawer .dbconsole-block.is-fresh {
    box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.35), 0 1px 3px rgba(28, 47, 84, 0.08);
}

.dbconsole-drawer .dbconsole-block-anim-move {
    transition: transform 0.55s ease;
}

.dbconsole-drawer .dbconsole-block-anim-enter-active {
    overflow: hidden;
    transition: max-height 0.8s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.6s ease,
        transform 0.6s ease, margin-bottom 0.8s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.dbconsole-drawer .dbconsole-block-anim-enter-from {
    max-height: 0;
    margin-bottom: 0;
    opacity: 0;
    transform: translateY(-24px);
}

.dbconsole-drawer .dbconsole-block-anim-enter-to {
    max-height: 1200px;
}

/* 移除：快一点，向上淡出并收起，增强操作反馈 */
.dbconsole-drawer .dbconsole-block-anim-leave-active {
    overflow: hidden;
    transition: max-height 0.28s ease, opacity 0.22s ease, transform 0.22s ease,
        margin-bottom 0.28s ease;
}

.dbconsole-drawer .dbconsole-block-anim-leave-from {
    max-height: 1200px;
}

.dbconsole-drawer .dbconsole-block-anim-leave-to {
    max-height: 0;
    margin-bottom: 0;
    opacity: 0;
    transform: translateY(-16px);
}

/* 块序号标志（卡片左上角） */
.dbconsole-drawer .dbconsole-block__no {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 999px;
    background-color: #7d9fd0;
    color: #ffffff;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
}

.dbconsole-drawer .dbconsole-block__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-bottom: 1px solid #dfe7f1;
    background: linear-gradient(180deg, #f3f7fd 0%, #ffffff 100%);
}

.dbconsole-drawer .dbconsole-block__title {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.dbconsole-drawer .dbconsole-block__name {
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbconsole-drawer .dbconsole-block__db-select {
    width: 160px;
}

.dbconsole-drawer .dbconsole-block__ops {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-left: auto;
}

.dbconsole-drawer .dbconsole-block__input {
    padding: 8px 12px 0;
}

.dbconsole-drawer .dbconsole-block__input .el-textarea__inner {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    line-height: 18px;
}

.dbconsole-drawer .dbconsole-block__result {
    padding: 8px 12px 12px;
}

.dbconsole-drawer .dbconsole-null {
    color: #aab4c4;
    font-style: italic;
}

.dbconsole-drawer .dbconsole-shared {
    margin-bottom: 12px;
    border: 1px solid #cbd8e8;
    border-left: 3px solid #7d9fd0;
    border-bottom: 3px solid #7d9fd0;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 1px 3px rgba(31, 45, 61, 0.06);
    overflow: hidden;
}

.dbconsole-drawer .dbconsole-shared__head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    cursor: pointer;
}

.dbconsole-drawer .dbconsole-shared__head:hover {
    background-color: #f7f9fc;
}

.dbconsole-drawer .dbconsole-shared__arrow {
    flex: 0 0 auto;
    color: #aab4c4;
    transition: transform 0.2s;
}

.dbconsole-drawer .dbconsole-shared__arrow.is-open {
    transform: rotate(90deg);
}

.dbconsole-drawer .dbconsole-shared__info {
    flex: 1 1 auto;
    min-width: 0;
}

.dbconsole-drawer .dbconsole-shared__name {
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 标签另起一行（放在名称下方），避免挤压名称 */
.dbconsole-drawer .dbconsole-shared__meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
}

.dbconsole-drawer .dbconsole-shared__other {
    font-size: 12px;
    font-weight: 400;
    color: #aab4c4;
}

.dbconsole-drawer .dbconsole-shared__desc {
    font-size: 12px;
    color: #8a94a6;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 操作菜单触发按钮 */
.dbconsole-drawer .dbconsole-shared__ops {
    flex: 0 0 auto;
}

/* 操作菜单项配色：沿用 Element Plus 主题色，不另行调色 */
.dbconsole-drawer .dbconsole-mi--load {
    color: var(--el-color-primary);
}

.dbconsole-drawer .dbconsole-mi--share {
    color: var(--el-color-success);
}

.dbconsole-drawer .dbconsole-mi--delete {
    color: var(--el-color-danger);
}

/* 被禁用的菜单项统一置灰，避免主题色盖住禁用态 */
.dbconsole-drawer .dbconsole-mi.is-disabled {
    color: var(--el-text-color-disabled);
}

.dbconsole-drawer .dbconsole-shared__body {
    padding: 6px 10px 10px;
    border-top: 1px dashed #eef1f6;
}

.dbconsole-drawer .dbconsole-shared__hint {
    padding: 6px 0;
    font-size: 12px;
    color: #aab4c4;
}

.dbconsole-drawer .dbconsole-fav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    /* 相对所属 SQL 块再向右缩进一档，形成「块 → 收藏」的递进关系 */
    margin-left: 14px;
    border-left: 2px solid #e3eaf5;
    padding: 6px 8px 6px 10px;
    border-radius: 0 8px 8px 0;
}

.dbconsole-drawer .dbconsole-fav:hover {
    background-color: #f7f9fc;
}

.dbconsole-drawer .dbconsole-fav__name {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #1f2d3d;
}

.dbconsole-drawer .dbconsole-fav__meta {
    font-size: 12px;
    color: #aab4c4;
}

.dbconsole-drawer .dbconsole-view-head {
    margin-bottom: 12px;
}

.dbconsole-drawer .dbconsole-view-name {
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbconsole-drawer .dbconsole-view-desc {
    margin-left: 10px;
    font-size: 13px;
    color: #8a94a6;
}

/* 收藏详情工具条：弹窗会 teleport 到 body，这里不加祖先选择器，确保一定命中 */
.dbconsole-view-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
}

.dbconsole-view-search {
    width: 260px;
}

.dbconsole-view-match {
    font-size: 12px;
    color: #7d8ba1;
}

.dbconsole-view-hint {
    font-size: 12px;
    color: #aab4c4;
}

@media (max-width: 900px) {
    .dbconsole-drawer .dbconsole-body {
        flex-direction: column;
    }

    .dbconsole-drawer .dbconsole-right {
        flex: 0 0 auto;
        width: auto;
        /* 限制下方「SQL 块」整块的总高度（含标题/筛选/列表/分页），把更多纵向空间让给上方的 SQL 编辑器 */
        max-height: min(260px, 38vh);
        border-left: 0;
        /* 上下排列时的分割线：加深一档，比桌面端的左边框更明显 */
        border-top: 2px solid #5805ff;
    }

    .dbconsole-drawer .dbconsole-right__list {
        /* 高度交给父级 max-height 决定，自身只负责滚动 */
        flex: 1 1 auto;
        min-height: 0;
        max-height: none;
    }
}
</style>

<!-- 弹窗样式（复用 dbman-dialog 前缀，与其他页面一致） -->
<style>
.dbman-dialog .dbman-switch-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.dbman-dialog .dbman-tips {
    font-size: 12px;
    color: #aab4c4;
}

.dbman-dialog__foot {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.dbman-dialog__foot .el-button {
    min-width: 96px;
    height: 38px;
    margin-left: 0;
    border-radius: 10px;
}
</style>
